/**
 * RTK Query endpoints for the Customer resource.
 *
 * Targets the CustomerGridViewSet at /api/odl-app/customers/ which exposes
 * full CRUD (except destroy — MethodNotAllowed; customers are deactivated,
 * not deleted) plus sub-resource @actions for logo, integrations,
 * thirdparties, clients, and resellers.
 *
 * Hooks exported:
 *   useRetrieveCustomerQuery         — GET  customers/{id}/
 *   useCreateCustomerMutation        — POST customers/
 *   usePatchCustomerMutation         — PATCH customers/{id}/
 *   useUploadCustomerLogoMutation    — PATCH customers/{id}/ (multipart)
 *   useDeleteCustomerLogoMutation    — DELETE customers/{id}/logo/
 *   useCreateCustomerIntegrationMutation — POST customers/{id}/integrations/
 *   usePatchCustomerIntegrationMutation  — PATCH customers/{id}/integrations/{integrationId}/
 *   useBulkEditThirdPartiesMutation  — POST customers/{id}/thirdparties/
 *   usePutCustomerClientsMutation    — PUT customers/{id}/clients/
 *   usePutCustomerResellersMutation  — PUT customers/{id}/resellers/
 */

// eslint-disable-next-line no-restricted-imports -- documented queryFn escape hatch for multipart uploads outside axiosBaseQuery
import apiClient from '@sophocles/http-common';

import { entitiesApi } from './entitiesApi';

entitiesApi.injectEndpoints({
  endpoints: build => ({
    retrieveCustomer: build.query({
      query: ({ id }) => ({ url: `customers/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'Customer', id }],
    }),

    createCustomer: build.mutation({
      query: ({ body }) => ({ url: 'customers/', method: 'post', data: body }),
      // The create endpoint returns the customer bare, not wrapped in the CRUD
      // {data:...} envelope the retrieve/patch use — tolerate both so the
      // caller always gets the customer object (and its id) back.
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    patchCustomer: build.mutation({
      query: ({ id, body }) => ({ url: `customers/${id}/`, method: 'patch', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Customer', id: 'LIST' },
        { type: 'Customer', id },
      ],
    }),

    // Logo upload — multipart PATCH to the consolidated customer-settings
    // endpoint (the customers grid is read+create only; settings own edits).
    // Uses queryFn so FormData bypasses the JSON-only axiosBaseQuery.
    uploadCustomerLogo: build.mutation({
      queryFn: async ({ id, file }) => {
        try {
          const formData = new FormData();
          formData.append('general.logo', file);
          const res = await apiClient.patch(`/odl-app/customer-settings/${id}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { data: res.data?.data ?? res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Customer', id }],
    }),

    deleteCustomerLogo: build.mutation({
      query: ({ id }) => ({ url: `customers/${id}/logo/`, method: 'delete' }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Customer', id }],
    }),

    createCustomerIntegration: build.mutation({
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/integrations/`,
        method: 'post',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
    }),

    patchCustomerIntegration: build.mutation({
      query: ({ customerId, integrationId, body }) => ({
        url: `customers/${customerId}/integrations/${integrationId}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
    }),

    bulkEditThirdParties: build.mutation({
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/thirdparties/`,
        method: 'post',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
    }),

    putCustomerClients: build.mutation({
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/clients/`,
        method: 'put',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
    }),

    putCustomerResellers: build.mutation({
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/resellers/`,
        method: 'put',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
    }),
  }),
});

export const {
  useRetrieveCustomerQuery,
  useCreateCustomerMutation,
  usePatchCustomerMutation,
  useUploadCustomerLogoMutation,
  useDeleteCustomerLogoMutation,
  useCreateCustomerIntegrationMutation,
  usePatchCustomerIntegrationMutation,
  useBulkEditThirdPartiesMutation,
  usePutCustomerClientsMutation,
  usePutCustomerResellersMutation,
} = entitiesApi;
